// Maps Stripe card error / decline codes to clear, actionable, user-facing
// messages.
//
// References:
//   - https://docs.stripe.com/declines/codes
//   - https://docs.stripe.com/error-handling
//
// Stripe's own `error.message` is usually reasonable, but mapping the codes
// ourselves lets us:
//   - give more actionable guidance (what the customer should do next), and
//   - avoid leaking fraud-related decline reasons. Stripe advises presenting
//     lost/stolen/fraudulent declines as a generic decline so we don't tip off
//     potential fraud.

export interface StripeLikeError {
  type?: string;
  code?: string;
  decline_code?: string;
  message?: string | null;
}

// Shown when the browser can't reach Stripe at all (ad blocker / VPN / firewall).
export const PAYMENT_CONNECTION_ERROR =
  "We couldn't reach our payment processor. This is usually caused by an ad blocker, privacy extension, VPN, or network firewall. Please disable any ad blockers for this site or try a different network, then try again.";

// Generic fallback when we have no better information.
const GENERIC_ERROR =
  "Something went wrong while processing your payment. Please try again.";

// Stripe advises against revealing the specific reason for these declines, so
// unknown/sensitive decline codes fall back to this generic decline message.
const GENERIC_DECLINE =
  "Your card was declined. Please contact your card issuer for more information, or use a different payment method.";

// Keyed by Stripe `code` and/or `decline_code`. Where a value appears as both a
// code and a decline code (e.g. `expired_card`), the message is the same, so a
// single map is sufficient.
const MESSAGES_BY_CODE: Record<string, string> = {
  card_declined: GENERIC_DECLINE,
  insufficient_funds:
    "Your card has insufficient funds. Please use a different card or payment method.",
  card_velocity_exceeded:
    "Your card has reached a balance, credit, or transaction limit. Please contact your card issuer or use a different card.",
  withdrawal_count_limit_exceeded:
    "Your card has reached its balance or credit limit. Please use a different payment method.",
  expired_card: "Your card has expired. Please use a different card.",
  incorrect_cvc:
    "The card's security code (CVC) is incorrect. Please re-enter it and try again.",
  invalid_cvc:
    "The card's security code (CVC) is incorrect. Please re-enter it and try again.",
  incorrect_number:
    "The card number is incorrect. Please re-enter it and try again.",
  invalid_number:
    "The card number is incorrect. Please re-enter it and try again.",
  incorrect_zip:
    "The billing postal code is incorrect. Please re-enter it and try again.",
  incorrect_address:
    "The billing address is incorrect. Please re-enter it and try again.",
  invalid_expiry_month:
    "The card's expiration date is invalid. Please re-enter it and try again.",
  invalid_expiry_year:
    "The card's expiration date is invalid. Please re-enter it and try again.",
  card_not_supported:
    "This card doesn't support this type of purchase. Please contact your card issuer or use a different card.",
  currency_not_supported:
    "This card doesn't support this currency. Please use a different card.",
  invalid_account:
    "There's a problem with this card or the account it's connected to. Please contact your card issuer or use a different card.",
  new_account_information_available:
    "There's a problem with this card or the account it's connected to. Please contact your card issuer or use a different card.",
  invalid_amount:
    "This payment amount isn't allowed for this card. Please contact your card issuer or use a different card.",
  duplicate_transaction:
    "This looks like a duplicate of a recent payment. Please check your account before trying again.",
  processing_error:
    "An error occurred while processing your card. Please wait a moment and try again.",
  issuer_not_available:
    "We couldn't reach your card issuer to authorize the payment. Please try again in a few moments.",
  reenter_transaction: "The payment couldn't be processed. Please try again.",
  authentication_required:
    "Your bank requires additional authentication for this card. Please try again and complete the verification.",
  testmode_decline:
    "A test card was used. Please use a real card to complete your purchase.",
};

/**
 * Convert a Stripe error object (e.g. from `stripe.createPaymentMethod`) into a
 * friendly, actionable message. Falls back to Stripe's own message and finally
 * to a generic message.
 */
export function getStripeErrorMessage(error?: StripeLikeError | null): string {
  if (!error) return GENERIC_ERROR;

  if (error.type === "api_connection_error") return PAYMENT_CONNECTION_ERROR;

  // decline_code is the most specific signal when a card is declined.
  if (error.decline_code) {
    return MESSAGES_BY_CODE[error.decline_code] || GENERIC_DECLINE;
  }

  if (error.code && MESSAGES_BY_CODE[error.code]) {
    return MESSAGES_BY_CODE[error.code];
  }

  // Stripe's own message is fine for validation / incomplete-field errors, but
  // sanitize it in case the backend passed through a serialized object.
  return getStripeMessageFromString(error.message);
}

/**
 * Convert a raw error message string (e.g. from a backend callable response)
 * into something safe to show a customer. The backend occasionally returns a
 * serialized Stripe object for failed payments — never surface raw JSON.
 */
export function getStripeMessageFromString(message?: string | null): string {
  if (!message) return GENERIC_ERROR;

  const trimmed = message.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    return GENERIC_DECLINE;
  }

  return message;
}
